import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { X, Phone, Building2, MapPin, Users, Loader2 } from "lucide-react";
import { updateUserPhone, updateCompanyProfile } from "@/services/diagnosticService";

const INDUSTRY_OPTIONS = [
  { value: "btp", label: "BTP" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "commerce", label: "Commerce" },
  { value: "services", label: "Services" },
  { value: "autre", label: "Autre" },
];

const SIZE_OPTIONS = [
  { value: "auto-entrepreneur", label: "Auto-entrepreneur" },
  { value: "1-5", label: "1-5 salariés" },
  { value: "6-10", label: "6-10 salariés" },
  { value: "11-20", label: "11-20 salariés" },
  { value: "20+", label: "20+ salariés" },
];

export default function ProfileCompletionModal({ user, company, onComplete, onSkip }) {
  const [formData, setFormData] = useState({
    phone: "",
    industry: company?.industry || "",
    size_range: company?.size_range || "",
    city: company?.city || "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // At least one field should be filled
    const hasData = formData.phone || formData.industry || formData.size_range || formData.city;
    if (!hasData) {
      toast.error("Veuillez remplir au moins un champ");
      return;
    }

    setLoading(true);
    try {
      // Update phone in users table
      if (formData.phone) {
        await updateUserPhone(user.id, formData.phone);
      }

      // Update company profile
      if (company?.id && (formData.industry || formData.size_range || formData.city)) {
        await updateCompanyProfile(company.id, {
          industry: formData.industry || undefined,
          size_range: formData.size_range || undefined,
          city: formData.city || undefined,
        });
      }

      toast.success("Profil mis à jour !");
      onComplete();
    } catch (err) {
      toast.error(err.message || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="relative w-full max-w-md bg-[#0F2545] rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold text-white">
                Complétez votre profil
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Ces informations nous aident à personnaliser votre rapport
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-slate-300 text-sm flex items-center gap-2">
              <Phone size={14} className="text-[#59C3A6]" />
              Numéro de téléphone
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="06 12 34 56 78"
              className="bg-[#0B1F3B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] h-11"
            />
          </div>

          {/* Industry */}
          <div className="space-y-2">
            <Label htmlFor="industry" className="text-slate-300 text-sm flex items-center gap-2">
              <Building2 size={14} className="text-[#59C3A6]" />
              Secteur d'activité
            </Label>
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="w-full h-11 px-3 rounded-md bg-[#0B1F3B] border border-slate-700 text-white focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] focus:outline-none"
            >
              <option value="">Sélectionner...</option>
              {INDUSTRY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Size */}
          <div className="space-y-2">
            <Label htmlFor="size_range" className="text-slate-300 text-sm flex items-center gap-2">
              <Users size={14} className="text-[#59C3A6]" />
              Taille de l'entreprise
            </Label>
            <select
              id="size_range"
              name="size_range"
              value={formData.size_range}
              onChange={handleChange}
              className="w-full h-11 px-3 rounded-md bg-[#0B1F3B] border border-slate-700 text-white focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] focus:outline-none"
            >
              <option value="">Sélectionner...</option>
              {SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="city" className="text-slate-300 text-sm flex items-center gap-2">
              <MapPin size={14} className="text-[#59C3A6]" />
              Ville / Localisation
            </Label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Ex: Paris, Lyon, Marseille..."
              className="bg-[#0B1F3B] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] h-11"
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold py-3 h-auto rounded-full transition-all duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Enregistrement...
                </>
              ) : (
                "Valider et continuer"
              )}
            </Button>
            <button
              type="button"
              onClick={onSkip}
              className="text-slate-500 text-sm hover:text-slate-400 transition-colors"
            >
              Compléter plus tard
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
